import { Controller, FieldValues, Path, UseFormReturn } from 'react-hook-form';
import { Select } from 'antd';
import { cn } from '../../utils';
import { CustomRegisterOptions } from '../../types';

interface SelectInputProps<T extends FieldValues> {
  name: Path<T>;
  control?: UseFormReturn<T>['control'];
  form?: UseFormReturn<T>;
  rules?: CustomRegisterOptions<T>;
  label?: string;
  formState?: UseFormReturn<T>['formState'];
  addOnClass?: string;
  config?: Record<string, any>;
  customConfig?: Record<string, any>;
  placeholder?: string;
  options?: any[];
  disabled?: boolean;
  addOnContainerClass?: string;
  /** Called after the form field value is updated. */
  onChange?: (value: unknown) => void;
}

/**
 * A reusable component for rendering a select input field with validation and error handling.
 *
 * @template T - The type of the form values.
 * @param {SelectInputProps<T>} props - The properties for the SelectInput component.
 * @param {Path<T>} props.name - The name of the input field.
 * @param {UseFormReturn<T>['control']} [props.control] - The control object from react-hook-form.
 * @param {UseFormReturn<T>} [props.form] - The form object from react-hook-form.
 * @param {CustomRegisterOptions<T>} [props.rules] - The validation rules for the input field.
 * @param {string} [props.label] - The label for the input field.
 * @param {UseFormReturn<T>['formState']} [props.formState] - The form state from react-hook-form.
 * @param {string} [props.addOnClass] - Additional Classes for the select field
 * @param {string} [props.addOnContainerClass] - Additional Classes for the select field container
 * @param {Record<string, any>} [props.config={}] - Additional configuration for the select field.
 * @returns {JSX.Element} - The rendered SelectInput component.
 */
const SelectInput = <T extends FieldValues>({
  name,
  control,
  form,
  rules,
  label,
  formState,
  addOnClass = '',
  config = {},
  customConfig,
  placeholder,
  options,
  disabled,
  addOnContainerClass = '',
  onChange,
}: SelectInputProps<T>) => {
  const effectiveControl = control || form?.control;
  const effectiveFormState = formState || form?.formState;
  const errors = (effectiveFormState?.errors || {}) as Record<string, any>;

  if (!name || !effectiveControl) {
    return null;
  }

  const mergedConfig = {
    ...(customConfig || {}),
    ...(placeholder ? { placeholder } : {}),
    ...(options ? { options } : {}),
    ...(disabled !== undefined ? { disabled } : {}),
    ...config,
  };

  return (
    <div className={cn('flex flex-col gap-1', addOnContainerClass)}>
      {label && (
        <label className="text-xs font-medium text-text-100 whitespace-nowrap">
          {label}
        </label>
      )}
      <Controller
        name={name}
        control={effectiveControl}
        rules={rules}
        render={({ field }) => (
          <Select
            className={addOnClass}
            {...field}
            {...mergedConfig}
            onChange={selected => {
              const value = selected === undefined ? null : selected;
              field.onChange(value);
              onChange?.(value);
            }}
          />
        )}
      />
      <div
        className={cn(
          'text-xs text-red-500',
          { visible: errors[name] },
          { invisible: !errors[name] },
        )}
      >
        {errors[name] && `${errors[name]?.message}`}
      </div>
    </div>
  );
};

export default SelectInput;

